import { observer } from "mobx-react";
import { useState, useMemo } from "react";
import styles from "./styles/AccordionTerrace.module.scss";
import FontAwesome from "../../../utilities/FontAwesome";
import Markdown from "../../../utilities/Markdown";
import dynamic from "next/dynamic";
import { SocialItemModalNavigationProps } from "../../../modals/SocialItemModal";
import classNames from "classnames";
import { useStores } from "../../../../stores/useStores";
import WatsonApi from "../../../../backends/WatsonApi";

const AccordionItemEditor = dynamic(() => import("./AccordionItemEditor"));

const AccordionItem = observer(
  (
    props: {
      item: IAccordionItem;
      index: number;
      blockObj: IAccordionTerrace;
      handleCancel: () => void;
      accordionList: IAccordionItem[];
      expanded: boolean;
      toggleExpansion: (id: number) => void;
    } & SocialItemModalNavigationProps,
  ) => {
    const { userStore, modalStore, organizationStore } = useStores();
    const [editing, setEditing] = useState(false);
    const [addSubAccordion, setAddSubAccordion] = useState<boolean>(false);

    const isTopLevel = useMemo(() => {
      return props.item.path.split(".").length === 1;
    }, []);

    const accordionItemClassName = classNames({
      [styles.accordionItem]: true,
      [styles.accordionItemSubItem]: !isTopLevel,
    });

    const onClick = (e) => {
      e.stopPropagation();
      modalStore.addModal({
        type: "accordion",
        itemObj: props.item,
        blockObj: props.blockObj,
        handleSave: handleSave,
        accordionList: props.accordionList,
        getItemAtIndex: props?.getItemAtIndex,
        totalItemCount: props?.totalItemCount,
        initialItemIndex: props?.initialItemIndex,
      });
    };

    const addAccordionItem = (e) => {
      e.stopPropagation();
      if (!props.expanded) props.toggleExpansion(props.item.id);
      setAddSubAccordion(true);
    };

    const handleCancel = () => {
      setAddSubAccordion(false);
    };

    const onClickEvent = (e) => {
      isTopLevel
        ? props.toggleExpansion(props.item.id)
        : props.item.has_content
          ? onClick(e)
          : undefined;
    };

    function onKeyDown(event) {
      if (event.key === "Enter") {
        onClickEvent(event);
      } else if (event.key === "Escape" && props.expanded) {
        onClickEvent(event);
      }
    }

    const handleSave = async (
      item: IAccordionItemEditing,
    ): Promise<IAccordionItem> => {
      const client = await WatsonApi();
      const response =
        await client.apis.organizations.organizations_terraces_accordion_items_partial_update(
          {
            organization_pk: props.blockObj.organization_id,
            terrace_pk: props.blockObj.id,
            id: (item as IAccordionItem)?.id,
            expand: "content_html",
            data: {
              title: item.title,
              description: item.description,
              content_html: item.content_html,
              modal_enabled: item.modal_enabled,
            },
          },
        );
      const updatedItem = JSON.parse(response.data);

      props.blockObj.items = (props.blockObj.items as IAccordionItem[]).map(
        (accordionItem) => {
          return accordionItem.id === (item as IAccordionItem).id
            ? updatedItem
            : accordionItem;
        },
      );
      setEditing(false);
      return updatedItem;
    };

    const handleSaveSubItem = async (
      item: IAccordionItemEditing,
    ): Promise<IAccordionItem> => {
      const parentPath = props.item.path;
      const siblings = props.blockObj.items.filter((item) => {
        return (
          item.path &&
          item.path.startsWith(parentPath + ".") &&
          item.path.split(".").length === parentPath.split(".").length + 1
        );
      });
      let lastSiblingNum = 0;
      if (siblings.length > 0) {
        const lastSiblingPathParts =
          siblings[siblings.length - 1].path.split(".");
        lastSiblingNum = parseInt(
          lastSiblingPathParts[lastSiblingPathParts.length - 1],
        );
      }

      const newChildIndex = lastSiblingNum + 1;
      const paddedIndex = String(newChildIndex).padStart(3, "0");
      const path = `${parentPath}.${paddedIndex}`;
      const client = await WatsonApi();

      const response =
        await client.apis.organizations.organizations_terraces_accordion_items_create(
          {
            organization_pk: props.blockObj.organization_id,
            terrace_pk: props.blockObj.id,
            data: {
              title: item.title,
              description: item.description,
              content_html: item.content_html,
              modal_enabled: item.modal_enabled,
              path,
            },
          },
        );
      const updatedItem = JSON.parse(response.data);
      (props.blockObj.items as IAccordionItem[]).push(updatedItem);
      props.blockObj.items.sort((a, b) => a.path.localeCompare(b.path));
      setAddSubAccordion(false);
      return updatedItem;
    };

    if (editing) {
      return (
        <div className={styles.accordionItem}>
          <AccordionItemEditor
            blockObj={props.blockObj}
            index={props.index}
            item={props.item}
            handleSave={handleSave}
            handleCancel={() => {
              props.handleCancel();
              setEditing(false);
            }}
          />
        </div>
      );
    } else {
      return (
        <div className={accordionItemClassName}>
          <div
            onKeyDown={onKeyDown}
            tabIndex={0}
            onClick={(e) => onClickEvent(e)}
          >
            <div className={styles.accordionItemTitle}>
              <div className={styles.fullWidthButton}>
                <div>
                  <span>{props.item.title}</span>
                  {userStore.isEditor && !editing && (
                    <div className={styles.editItemButton}>
                      <button
                        key="edit"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditing(!editing);
                        }}
                        disabled={editing}
                        title={editing ? "Click to save" : "Click to edit"}
                        aria-label={editing ? "Click to save" : "Click to edit"}
                      >
                        <span
                          className="fas fa-pencil-alt"
                          role="presentation"
                        />
                      </button>
                      {organizationStore.organization.billing_products
                        ?.docmersion &&
                        !addSubAccordion &&
                        userStore.isEditor &&
                        isTopLevel && (
                          <button
                            style={{ marginLeft: 10, marginBottom: 0 }}
                            title={"Add Accordion Sub Item"}
                            aria-label={"Add Accordion Sub Item"}
                            onClick={addAccordionItem}
                          >
                            <FontAwesome
                              ariaHidden={false}
                              prefix={"fas"}
                              name={"fa-plus"}
                            />
                            &nbsp;
                            {"Add Sub-Item"}
                          </button>
                        )}
                    </div>
                  )}
                </div>
                {isTopLevel && (
                  <div className={styles.expandCollapseButton}>
                    <span
                      className={`${styles.icon} ${
                        props.expanded ? styles.rotated : ""
                      }`}
                    >
                      <FontAwesome
                        prefix="fas"
                        fixedWidth
                        name={props.expanded ? "fa-xmark" : "fa-chevron-down"}
                        ariaHidden
                      />
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div
              className={`${styles.accordionItemContent} ${
                props.expanded ? styles.open : styles.closed
              }`}
            >
              {props.item.description && (
                <div className={styles.markDownWrapper}>
                  <Markdown withStyles={true} source={props.item.description} />
                </div>
              )}
              {organizationStore.organization.billing_products?.docmersion && (
                <div>
                  {props.item.modal_enabled && (
                    <button
                      className={styles.viewMoreButton}
                      title={"View Accordion Content"}
                      aria-label={"View Accordion Content"}
                      onClick={onClick}
                    >
                      View More
                      <FontAwesome
                        prefix={"fas"}
                        name={"fa-arrow-right"}
                        fixedWidth
                      />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {organizationStore.organization.billing_products?.docmersion &&
            props.expanded &&
            props.item.children?.length > 0 &&
            props.item.children.map((item) => {
              const itemIndex = props.blockObj.items.findIndex(
                (listItem) => listItem.id === item.id,
              );
              return (
                <AccordionItem
                  key={item.id}
                  totalItemCount={props.blockObj.items.length}
                  getItemAtIndex={(idx) => {
                    return props.blockObj.items.length >= idx
                      ? props.blockObj.items[idx]
                      : null;
                  }}
                  initialItemIndex={itemIndex}
                  item={props.blockObj.items[itemIndex]}
                  accordionList={props.item.children}
                  index={itemIndex}
                  blockObj={props.blockObj}
                  handleCancel={handleCancel}
                  expanded={true}
                  toggleExpansion={props.toggleExpansion}
                />
              );
            })}
          {addSubAccordion && (
            <div className={styles.subItemEditor}>
              <AccordionItemEditor
                blockObj={props.blockObj}
                index={props.index}
                handleSave={handleSaveSubItem}
                handleCancel={() => {
                  setAddSubAccordion(false);
                }}
              />
            </div>
          )}
        </div>
      );
    }
  },
);

export default AccordionItem;
